import { EnvironmentInjector, Injectable, inject } from '@angular/core';
import { fromJson, toJson } from '@bufbuild/protobuf';
import { NbToastrService } from '@nebular/theme';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  CollectionNames,
  UserServiceCreateResponseJson,
  UserServiceCreateResponseSchema,
  UserServiceListRequestJson,
  UserServiceListRequestSchema,
  defaultApiSerializeJsonOptions,
} from '@vmspdb/definitions';
import { GlobalErrorHandlerService } from '@vmspdb/ui/common';
import { AuthRunEffects } from '@vmspdb/ui/store';
import { EMPTY, Observable, of, tap } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import { UserRepositoryService, withSearchSync } from '../../repositories';
import { UtilitiesFacade } from '../utilities/utilities.facade';
import { PaginatedUserActions } from './paginated-user.actions';

@Injectable()
export class PaginatedUserEffects extends AuthRunEffects {
  private injector = inject(EnvironmentInjector);
  private errors = inject(GlobalErrorHandlerService);
  private repository = inject(UserRepositoryService);
  private notifications = inject(NbToastrService);
  private utilities = inject(UtilitiesFacade);

  listPaginatedUsers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        PaginatedUserActions.listPaginatedUsers,
        PaginatedUserActions.cancel,
      ),
      switchMap((action) =>
        action.type === PaginatedUserActions.cancel.type
          ? EMPTY
          : of(action).pipe(this.performSearch()),
      ),
    );
  });

  performSearch() {
    return (
      source: Observable<{
        params: UserServiceListRequestJson;
      }>,
    ) =>
      source.pipe(
        debounceTime(250),
        withSearchSync(CollectionNames.Users, this.injector),
        tap(() => this.utilities.queue()),
        switchMap((payload) => {
          return this.repository.list(
            fromJson(UserServiceListRequestSchema, payload.params),
          );
        }),
        tap((data) => {
          if (data.degraded) {
            this.errors.reportSearchDegradation(CollectionNames.Users);
            this.notifications.warning(
              'There was an error with the search server for fetching users.',
              'Search',
              {
                duration: 5000,
              },
            );
          }
        }),
        map((data) =>
          PaginatedUserActions.listPaginatedUsersSuccess({
            data: {
              ...data,
              items: (data.items ?? []).map(
                (item) =>
                  toJson(
                    UserServiceCreateResponseSchema,
                    item,
                    defaultApiSerializeJsonOptions,
                  ) as UserServiceCreateResponseJson,
              ),
            },
          }),
        ),
        catchError((error) => {
          this.errors.reportCaughtError(error);
          return of(PaginatedUserActions.listPaginatedUsersFailure(error));
        }),
        tap(() => this.utilities.complete()),
      );
  }

  constructor() {
    const actions$ = inject(Actions);

    super(actions$);
  }
}
